<div class="payment-dashboard">
  <div class="revenue-dashboard-header">
    <div class="header-left">
      <div class="title-section">
        <div class="title-row">
          <h2 class="revenue-title">Payment Dashboard</h2>
        </div>
        <p class="revenue-description">
          Monitor card payments, revenue, and refunds across your locations.
        </p>
      </div>
    </div>
    <app-payments-toolbar-filters
      [locationOptions]="locationOptions"
      [selectedLocationId]="selectedLocationId"
      [selectedLocationLabel]="selectedLocationLabel"
      (locationChange)="onLocationChange($event)"
      [statusOptions]="ledgerStatuses"
      [selectedStatuses]="selectedLedgerStatuses"
      (selectedStatusesChange)="selectedLedgerStatuses = $event; onStatusChange()"
      [selectedPeriod]="selectedPeriod"
      [customDateRange]="customDateRange"
      (dateRangeApply)="onDateRangeApply($event)"
      statusSelectInputId="ledgerStatus"
      [exportLoading]="exportLoading"
      (exportClick)="onExport()"
    >
    </app-payments-toolbar-filters>
  </div>

  <div class="dashboard-stats-container" [class.is-loading]="metricsLoading">
    <div class="dashboard-stats-grid dashboard-stats-grid--primary" *ngIf="!metricsLoading">
      <app-customer-stats-card
        *ngFor="let stat of primaryPaymentStats; trackBy: trackByStatTitle"
        [data]="stat"
      >
      </app-customer-stats-card>
    </div>
    <div class="dashboard-stats-grid dashboard-stats-grid--secondary" *ngIf="!metricsLoading">
      <ng-container *ngFor="let stat of secondaryPaymentStats; trackBy: trackByStatTitle">
        <!-- CWS-2012: Pending Payouts reads Stripe on its own request. -->
        <div
          class="metric-card-skeleton"
          *ngIf="showPendingPayoutsSkeleton(stat); else statCard"
        ></div>
        <ng-template #statCard>
          <app-customer-stats-card [data]="stat"></app-customer-stats-card>
        </ng-template>
      </ng-container>
    </div>
    <ng-container *ngIf="metricsLoading">
      <div
        class="dashboard-stats-grid dashboard-stats-grid--primary dashboard-stats-grid--skeleton"
      >
        <div class="metric-card-skeleton" *ngFor="let _ of primaryMetricSkeletonItems"></div>
      </div>
      <div
        class="dashboard-stats-grid dashboard-stats-grid--secondary dashboard-stats-grid--skeleton"
      >
        <div class="metric-card-skeleton" *ngFor="let _ of secondaryMetricSkeletonItems"></div>
      </div>
    </ng-container>
  </div>

  <div class="ledger-table-wrap">
    <div class="table-heading">
      <h3 class="table-title">Transaction Ledger</h3>
      <p class="table-description">
        Every card payment in the selected period, dated when it was charged, including failed,
        refunded and membership plan-change charges. Total Revenue counts only paid orders and
        membership renewals, so the ledger total won't always match it.
      </p>
    </div>

    <div class="table-scroll-wrapper" *ngIf="tableLoading">
      <app-loading-table-shimmer
        [columns]="shimmerColumns"
        [rows]="10"
        [showHeader]="false"
        className="scrollable"
      >
      </app-loading-table-shimmer>
    </div>

    <app-empty-result
      *ngIf="!tableLoading && ledgerRows.length === 0"
      image="/assets/images/empty_screen.png"
      title="No transactions found"
      description="Card transactions will appear here once payments are processed."
      [showAction]="false"
    >
    </app-empty-result>

    <div class="table-scroll-wrapper" *ngIf="!tableLoading && ledgerRows.length > 0">
      <table mat-table [dataSource]="dataSource" class="mat-mdc-table payments-table">
        <!-- Transaction ID Column -->
        <ng-container matColumnDef="transactionId">
          <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Transaction ID</th>
          <td mat-cell *matCellDef="let row">
            <span class="mono">{{ row.transactionId }}</span>
          </td>
        </ng-container>

        <!-- Date & Time Column -->
        <ng-container matColumnDef="dateTime">
          <th mat-header-cell *matHeaderCellDef style="min-width: 150px">Date & Time</th>
          <td mat-cell *matCellDef="let row">
            {{ row.dateTime }}
          </td>
        </ng-container>

        <!-- Customer Name Column -->
        <ng-container matColumnDef="customerName">
          <th mat-header-cell *matHeaderCellDef style="min-width: 150px">Customer</th>
          <td mat-cell *matCellDef="let row">
            {{ row.customerName }}
          </td>
        </ng-container>

        <!-- Order Column -->
        <ng-container matColumnDef="order">
          <th mat-header-cell *matHeaderCellDef style="min-width: 120px">Order</th>
          <td mat-cell *matCellDef="let row">
            <a
              *ngIf="row.orderId"
              [routerLink]="['/orders']"
              [queryParams]="{ orderId: row.orderId }"
              class="order-link"
            >
              {{ row.customerIdentifier }}
            </a>
            <span *ngIf="!row.orderId">{{ row.customerIdentifier }}</span>
          </td>
        </ng-container>

        <!-- Wash Type Column -->
        <ng-container matColumnDef="washType">
          <th mat-header-cell *matHeaderCellDef style="min-width: 120px">Wash Type</th>
          <td mat-cell *matCellDef="let row">
            <span class="wash-type-badge" [ngClass]="row.washTypeClass">{{ row.washType }}</span>
          </td>
        </ng-container>

        <!-- Location Column -->
        <ng-container matColumnDef="locationName">
          <th mat-header-cell *matHeaderCellDef style="min-width: 150px">Location</th>
          <td mat-cell *matCellDef="let row">
            {{ row.locationName }}
          </td>
        </ng-container>

        <!-- Discount Column -->
        <ng-container matColumnDef="discount">
          <th mat-header-cell *matHeaderCellDef style="min-width: 160px">Discount</th>
          <td mat-cell *matCellDef="let row">
            {{ row.discountTerms }}
          </td>
        </ng-container>

        <!-- Order Amount Column -->
        <ng-container matColumnDef="orderAmount">
          <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Order Amount</th>
          <td mat-cell *matCellDef="let row">
            {{ row.orderAmount | currency: 'USD' : 'symbol' : '1.2-2' }}
          </td>
        </ng-container>

        <!-- Convenience Fee Column -->
        <ng-container matColumnDef="convenienceFee">
          <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Convenience Fee</th>
          <td mat-cell *matCellDef="let row">
            {{
              row.convenienceFee !== null
                ? (row.convenienceFee | currency: 'USD' : 'symbol' : '1.2-2')
                : '-'
            }}
          </td>
        </ng-container>

        <!-- Total Amount Column -->
        <ng-container matColumnDef="totalAmount">
          <th mat-header-cell *matHeaderCellDef style="min-width: 140px">
            <span class="ledger-header-with-tooltip">
              Total Amount
              <span
                class="info-icon"
                [matTooltip]="totalAmountHeaderTooltip"
                matTooltipPosition="above"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M7 0C3.134 0 0 3.134 0 7s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm0 12.833c-3.225 0-5.833-2.608-5.833-5.833S3.775 1.167 7 1.167 12.833 3.775 12.833 7 10.225 12.833 7 12.833z"
                    fill="#15172E"
                  />
                  <path
                    d="M7 3.5c-.483 0-.875.392-.875.875S6.517 5.25 7 5.25s.875-.392.875-.875S7.483 3.5 7 3.5zM7 6.125c-.483 0-.875.392-.875.875v2.625c0 .483.392.875.875.875s.875-.392.875-.875V7c0-.483-.392-.875-.875-.875z"
                    fill="#15172E"
                  />
                </svg>
              </span>
            </span>
          </th>
          <td mat-cell *matCellDef="let row">
            {{ row.totalAmount | currency: 'USD' : 'symbol' : '1.2-2' }}
          </td>
        </ng-container>

        <!-- Platform Fee Column -->
        <ng-container matColumnDef="platformFee">
          <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Platform Fee</th>
          <td mat-cell *matCellDef="let row">
            {{
              row.platformFee !== null
                ? (row.platformFee | currency: 'USD' : 'symbol' : '1.2-2')
                : '-'
            }}
          </td>
        </ng-container>

        <!-- Payment Method Column -->
        <ng-container matColumnDef="paymentMethod">
          <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Payment Method</th>
          <td mat-cell *matCellDef="let row">
            {{ row.paymentMethod }}
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef style="min-width: 120px">Status</th>
          <td mat-cell *matCellDef="let row">
            <span
              class="ledger-status"
              [ngClass]="row.statusClass"
              [matTooltip]="row.statusTooltip"
              matTooltipPosition="above"
              >{{ row.status }}</span
            >
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
      </table>
    </div>

    <app-pagination-bar
      *ngIf="paginationData.totalPages > 0"
      [paginationData]="paginationData"
      [itemsPerPageOptions]="[10, 25, 50, 100]"
      (pageChange)="onPageChange($event)"
      (itemsPerPageChange)="onItemsPerPageChange($event)"
    >
    </app-pagination-bar>
  </div>
</div>
